import { z } from "zod";

import { titleRegexTest } from "@/helpers/helpers";

// Type for writer selection with CAE/IPI number
type WriterForSelection = {
  writercode: string;
  caeipinumber?: string | null;
};

/**
 * Creates a dynamic Zod schema for song form validation
 * Includes site configuration-based validation rules
 */
export function createSongFormSchema(
  _siteConfigData: { ipicae_required?: boolean } | undefined,
  _selectedWriters: WriterForSelection[],
  t: (key: string) => string,
) {
  return z.object({
    // Song details
    title: z
      .string()
      .min(1, t("regex:song-validation-title-required") || "Song title is required")
      .max(200, t("regex:song-validation-title-max-length") || "Song title must be less than 200 characters")
      .refine((value) => {
        return titleRegexTest(value);
      }, t("regex:invalid-characters") || "Song title contains invalid characters"),

    durationMins: z
      .number()
      .min(0, t("regex:song-validation-duration-mins-negative") || "Duration minutes cannot be negative")
      .max(120, t("regex:song-validation-duration-mins-max") || "Duration minutes must be less than 120"),

    durationSecs: z
      .number()
      .min(0, t("regex:song-validation-duration-secs-negative") || "Duration seconds cannot be negative")
      .max(59, t("regex:song-validation-duration-secs-max") || "Duration seconds must be less than 60"),

    alternativeTitles: z
      .array(z.string())
      .refine((values) => {
        return values.every((value) => {
          if (!value || value.trim() === "")
            return true;
          return titleRegexTest(value);
        });
      }, t("regex:invalid-characters") || "One or more alternative titles contain invalid characters"),

    performingArtists: z
      .array(z.string().min(1, t("regex:song-performer-name-regex") || "Performing artist name is required"))
      .refine((values) => {
        // Filter out empty strings
        const nonEmptyValues = values.filter(v => v.trim() !== "");
        // Require at least one non-empty value
        return nonEmptyValues.length > 0;
      }, t("regex:song-performer-required") || "At least one performing artist is required"),

    // Remix and samples flags
    isRemix: z.boolean(),
    containsSamples: z.boolean(),

    // Writer validation - basic structure only, complex validations handled by business logic
    writers: z
      .array(z.object({
        writercode: z.string().min(1, t("regex:song-validation-writer-code-required") || "Writer code is required"),
        writername: z.string().min(1, t("regex:song-validation-writer-name-required") || "Writer name is required"),
        writershare: z
          .number()
          .min(0, t("regex:song-validation-writer-share-negative") || "Writer share cannot be negative")
          .max(100, t("regex:song-validation-writer-share-max") || "Writer share cannot exceed 100%")
          .refine((value) => {
            // Check decimal precision - max 2 decimal places
            if (value.toString().includes(".")) {
              const decimalPart = value.toString().split(".")[1];
              return decimalPart.length <= 2;
            }
            return true;
          }, t("regex:song-validation-writer-share-decimal-precision") || "Writer share cannot have more than 2 decimal places"),
        writerdesignationcode: z.number().min(1).max(3),
        righttocollect: z.boolean(),
      })),
    // Complex validations (min count, total shares, right to collect, CAE/IPI) are handled by business logic validation
  });
}

// Create dynamic type based on the schema
// Infer the TypeScript type from the schema for form validation
export type SongFormSchemaData = z.infer<ReturnType<typeof createSongFormSchema>>;

// Re-export the original SongFormData type from store for backwards compatibility
export type { SongFormData } from "../store/songCreationStore";
